import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    useMediaQuery
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import classNames from "classnames";
import toast from "react-hot-toast";
import { COPY_TARGETS_STALE_ERROR, IComment, toMmSs } from "./constants";
import { useVideoFeedback } from "./useVideoFeedback";
import { BrandProject, IProjectFiles } from "../constants";
import { filterRealFiles } from "../../helper";
import { ChannelOptionDisplay } from "../shared/ChannelAccordion";
import axiosInstant from "../../../../api";
import { getBrandProject } from "../../../../api/endpoints";
import { useChannelGroups } from "../../../../hooks/useChannelGroups";
import { DotLottleLoader } from "../../../../components/Loader";
import { PLAY_GLYPH, thumbSource } from "../../../../components/videoSection/videoThumb";
import { StatusPill } from "../../../../components/videoSection/StatusPill";
import { getVideoStatus } from "../../../../components/videoSection/videoStatus";
import { groupVariantOptions } from "../../../../components/videoSection/videoDetails";
import { formatVideoName } from "../../../admin/videos/hooks/useFetchVideos";
import { fancyTimeFormat } from "../../../admin/videos/adminVideosV2/helper";
import { IconChevronDown } from "../../../../utils/icons";
import { MentionText } from "../../../../components/Mentions/MentionText";

// Unchecked --muted, checked/partial --accent (the house checkbox, §11).
// disableRipple also drops MUI's only focus cue (the real input is invisible),
// so keyboard focus gets the global accent ring back explicitly.
const CHECK_SX = {
    p: 0,
    color: "var(--muted)",
    "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "var(--accent)" },
    "&.Mui-disabled": { color: "var(--muted)", opacity: 0.5 },
    "&.Mui-focusVisible": { outline: "2px solid var(--accent)", outlineOffset: 2, borderRadius: "4px" }
};

// Tree geometry: a small Checkbox with p:0 is 20px square, so the spine runs
// down its centre (x = 10) and each row's connector meets the spine there.
const ROW_INDENT = 30;
const SPINE_X = 9.5 - ROW_INDENT;

// Channels start open while the whole list stays short enough to scan.
const EXPAND_ALL_LIMIT = 24;

// Why a row is shown but can't be picked.
type DisabledReason = "source" | "applied" | "approved" | null;

interface Row {
    video: IProjectFiles;
    reason: DisabledReason;
}

interface Group {
    key: string;
    name: string;
    selections: any[];
    rows: Row[];
    enabledIds: number[];
}

interface Props {
    comment: IComment;
    onClose: () => void;
}

const sortById = (videos: IProjectFiles[]) => [...videos].sort((a, b) => Number(a.id) - Number(b.id));

const pluralVideos = (n: number) => `${n} video${n === 1 ? "" : "s"}`;

/**
 * "Apply to other videos": copies one review comment onto other delivered
 * videos of the project, picked per channel. Mounted once by VideoFeedback.
 */
export const ApplyCommentDialog: FC<Props> = ({ comment: openedComment, onClose }) => {
    const { project, video, onCopyComment, comments, getComments } = useVideoFeedback();
    // Read the comment from the live thread, so a refetch (after a copy, or a
    // socket echo) refreshes the videos it is already applied to.
    const comment = comments.find((c) => String(c.id) === String(openedComment.id)) || openedComment;
    // Videos the API refused as already holding this comment, until the
    // refetched thread says so itself.
    const [rejectedIds, setRejectedIds] = useState<number[]>([]);
    const isMobile = useMediaQuery("(max-width: 767.95px)");

    // The review context's project is scoped to the one video under review,
    // so the picker loads the full project for its siblings.
    const projectId = project?.id || video?.projectId;
    const [fullProject, setFullProject] = useState<BrandProject | null>(null);
    const [loadState, setLoadState] = useState<"loading" | "error" | "ready">("loading");
    const [selected, setSelected] = useState<Set<number>>(() => new Set());
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [submitting, setSubmitting] = useState(false);

    const isMounted = useRef(true);
    useEffect(
        () => () => {
            isMounted.current = false;
        },
        []
    );

    // The source comment's own file, and files that already hold a copy (the
    // API rejects both). Threads are per version (TDC-1733), so another
    // version of the same video is a fair target. Keyed on primitives —
    // parents may pass a fresh video object for the same file, which must
    // not refetch.
    const videoId = video?.id;
    const sourceFileId = comment.s3_file_id;
    const appliedIdsKey = [...(comment.applied_to || []).map(({ file }) => file?.id), ...rejectedIds].join(",");
    const getDisabledReason = useCallback(
        (f: IProjectFiles): DisabledReason => {
            if (f.id === videoId || f.id === sourceFileId) return "source";
            if (appliedIdsKey.split(",").includes(String(f.id))) return "applied";
            if (f.final === true) return "approved";
            return null;
        },
        [videoId, sourceFileId, appliedIdsKey]
    );

    const loadProject = useCallback(
        async (silent = false) => {
            if (!projectId) {
                setLoadState("error");
                return;
            }
            if (!silent) setLoadState("loading");
            try {
                const { data } = await axiosInstant.get(getBrandProject(projectId));
                if (!isMounted.current) return;
                setFullProject(data);
                setLoadState("ready");
            } catch (error) {
                if (!isMounted.current) return;
                if (!silent) setLoadState("error");
            }
        },
        // Only the project id: which rows are disabled changes as the thread
        // refreshes, and that must re-derive the rows, never refetch.
        [projectId]
    );

    useEffect(() => {
        loadProject();
    }, [loadProject]);

    const files = useMemo(() => filterRealFiles<IProjectFiles>(fullProject?.projectFiles), [fullProject]);
    const channels = useMemo(() => fullProject?.content_channels || [], [fullProject]);
    const { channelGroups, hasChannels } = useChannelGroups(channels, files);

    const groups = useMemo((): Group[] => {
        const base: Array<{ key: string; name: string; selections: any[]; videos: IProjectFiles[] }> = hasChannels
            ? channelGroups.map((g) => ({
                  key: g.key,
                  name: g.channel.name,
                  selections: g.channel.project_option_selections || [],
                  videos: g.videos as IProjectFiles[]
              }))
            : [{ key: "all", name: "", selections: [], videos: files }];
        return base
            .filter((g) => g.videos.length > 0)
            .map(({ videos, ...g }) => {
                const rows = sortById(videos).map((v) => ({ video: v, reason: getDisabledReason(v) }));
                return { ...g, rows, enabledIds: rows.filter((r) => !r.reason).map((r) => r.video.id) };
            });
    }, [channelGroups, hasChannels, files, getDisabledReason]);

    // Keep only picks that are still selectable whenever that set changes (a
    // refreshed list, a refused video, a thread refetch).
    const enabledKey = groups.flatMap((g) => g.enabledIds).join(",");
    useEffect(() => {
        const enabled = new Set(enabledKey.split(",").filter(Boolean).map(Number));
        setSelected((prev) => {
            const next = Array.from(prev).filter((id) => enabled.has(id));
            return next.length === prev.size ? prev : new Set(next);
        });
    }, [enabledKey]);

    const totalRows = groups.reduce((sum, g) => sum + g.rows.length, 0);
    const otherRows = groups.reduce((sum, g) => sum + g.rows.filter((r) => r.reason !== "source").length, 0);
    const totalEnabled = groups.reduce((sum, g) => sum + g.enabledIds.length, 0);
    const n = selected.size;
    const allSelected = totalEnabled > 0 && n === totalEnabled;
    const defaultExpanded = totalRows <= EXPAND_ALL_LIMIT;
    const isExpanded = (key: string) => expanded[key] ?? defaultExpanded;

    const setIds = (ids: number[], on: boolean) =>
        setSelected((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => {
                if (on) next.add(id);
                else next.delete(id);
            });
            return next;
        });

    const toggleAll = () => setIds(groups.flatMap((g) => g.enabledIds), !allSelected);

    const handleClose = () => {
        if (!submitting) onClose();
    };

    const handleApply = async () => {
        if (!onCopyComment || comment.id == null || !n) return;
        setSubmitting(true);
        try {
            const count = await onCopyComment(comment.id, Array.from(selected));
            toast.success(`Comment applied to ${pluralVideos(count)}`);
            if (isMounted.current) onClose();
        } catch (err: any) {
            if (!isMounted.current) return;
            toast.error(err?.message || "Couldn't apply the comment. Please try again.");
            setSubmitting(false);
            // A picked video was replaced or removed, or already got this
            // comment meanwhile: mark the refused ones (which drops them from
            // the selection, keeping the rest) and quietly refresh the list
            // and the thread.
            if (err?.message === COPY_TARGETS_STALE_ERROR) {
                const refused: number[] = err?.invalidFileIds || [];
                setRejectedIds((prev) => [...prev, ...refused]);
                getComments(video?.id);
                loadProject(true);
            }
        }
    };

    // Copies keep the original author, who may be the client or a guest.
    const author =
        comment.user?.displayName || comment.user?.firstName || comment.guest_name || "Guest";

    // tree: the row hangs off its channel's spine. Each row draws its own
    // stretch of spine (from the gap above it — or the header, for the first
    // row — down past its connector), so the spine ends at the last connector.
    const renderRow = ({ video: v, reason }: Row, tree?: { first: boolean; last: boolean }) => {
        const checked = selected.has(v.id);
        const disabled = Boolean(reason);
        const title = formatVideoName(v.title || v.name || v.key || "") || "Untitled video";
        const meta = [v.version ? `v${v.version}` : "", v.aspect_ratio || "", v.length ? fancyTimeFormat(v.length) : ""].filter(
            Boolean
        );
        const options = groupVariantOptions(v);
        const status = reason === "approved" ? getVideoStatus(v, fullProject) : null;

        return (
            <label
                key={v.id}
                title={title}
                className={classNames("relative flex items-center gap-[13px]", disabled ? "cursor-default" : "cursor-pointer")}
            >
                {tree && (
                    <>
                        <span
                            aria-hidden
                            className="absolute w-px bg-border-token"
                            style={{ left: SPINE_X, top: tree.first ? 0 : -8, bottom: tree.last ? "50%" : 0 }}
                        />
                        <span
                            aria-hidden
                            className="absolute top-1/2 h-px bg-border-token"
                            style={{ left: SPINE_X, width: -SPINE_X - 4 }}
                        />
                    </>
                )}
                <Checkbox
                    size="small"
                    disableRipple
                    checked={checked}
                    disabled={disabled}
                    onChange={(e) => setIds([v.id], e.target.checked)}
                    sx={CHECK_SX}
                />
                <span
                    className={classNames(
                        "flex-1 min-w-0 flex items-center gap-3 p-2.5 bg-surface border rounded-xl transition-colors duration-150",
                        // eslint-disable-next-line no-nested-ternary
                        checked ? "border-accent" : disabled ? "border-border-token opacity-60" : "border-border-token hover:border-accent"
                    )}
                >
                    <span className="relative w-[72px] h-[40px] shrink-0 rounded-lg bg-track text-muted flex items-center justify-center overflow-hidden">
                        {PLAY_GLYPH(16)}
                        <img
                            src={thumbSource(v)}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            className="absolute inset-0 w-full h-full object-cover"
                            onError={(e) => {
                                (e.currentTarget as HTMLImageElement).style.display = "none";
                            }}
                        />
                    </span>
                    <span className="flex-1 min-w-0 flex flex-col gap-1">
                        <span className="flex items-center gap-2 min-w-0">
                            <span className="font-bold text-text-1 truncate min-w-0" style={{ fontSize: 13.5, lineHeight: 1.3 }}>
                                {title}
                            </span>
                            {(reason === "source" || reason === "applied") && (
                                <span
                                    className="inline-flex items-center h-[22px] px-[9px] rounded-full bg-tint text-accent-text whitespace-nowrap shrink-0"
                                    style={{ fontSize: 11 }}
                                >
                                    {reason === "source" ? "This video" : "Already applied"}
                                </span>
                            )}
                            {status && <StatusPill status={status.key} label={status.label} />}
                        </span>
                        {meta.length > 0 && (
                            <span className="flex items-center gap-2 min-w-0 text-xs text-text-2 tabular-nums">
                                {meta.map((part, i) => (
                                    <React.Fragment key={part}>
                                        {i > 0 && <span className="shrink-0">·</span>}
                                        <span className="shrink-0">{part}</span>
                                    </React.Fragment>
                                ))}
                            </span>
                        )}
                        {options.length > 0 && (
                            <span className="flex flex-wrap gap-1.5">
                                {options.map((o) => (
                                    <span
                                        key={o.optionId}
                                        className="inline-flex items-center h-[22px] px-[9px] rounded-full bg-surface-2 text-text-1"
                                        style={{ fontSize: 11 }}
                                    >
                                        {o.optionName}: {o.displayValue}
                                    </span>
                                ))}
                            </span>
                        )}
                    </span>
                </span>
            </label>
        );
    };

    const renderGroup = (group: Group) => {
        const open = isExpanded(group.key);
        const m = group.enabledIds.length;
        const k = group.enabledIds.filter((id) => selected.has(id)).length;
        return (
            <Box key={group.key} className="flex flex-col">
                <Box className="flex items-start gap-3 pt-2.5 pb-1.5">
                    {/* The checkbox column carries the spine down to the first row. */}
                    <Box className="self-stretch flex flex-col items-center shrink-0">
                        <Checkbox
                        size="small"
                        disableRipple
                        checked={m > 0 && k === m}
                        indeterminate={k > 0 && k < m}
                        disabled={m === 0}
                        onChange={() => setIds(group.enabledIds, k < m)}
                        inputProps={{ "aria-label": `Select all in ${group.name}` }}
                        sx={CHECK_SX}
                        />
                        {open && <span aria-hidden className="flex-1 w-px mt-1 -mb-1.5 bg-border-token" />}
                    </Box>
                    <Box className="flex-1 min-w-0 flex flex-col gap-0.5">
                        <Box className="flex items-center gap-3 min-w-0">
                            <Typography
                                noWrap
                                sx={{ fontFamily: "CircularStd", fontWeight: 700, fontSize: "15px", color: "var(--text)" }}
                            >
                                {group.name}
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: "CircularStd",
                                    fontWeight: 450,
                                    fontSize: "13px",
                                    color: "var(--text-2)",
                                    whiteSpace: "nowrap"
                                }}
                            >
                                {m > 0 ? `${k} of ${m} selected` : pluralVideos(group.rows.length)}
                            </Typography>
                        </Box>
                        {group.selections.length > 0 && <ChannelOptionDisplay selections={group.selections} />}
                    </Box>
                    <button
                        type="button"
                        className="ml-auto -mt-1 w-7 h-7 shrink-0 rounded-lg hover:bg-surface-2 text-text-2 flex items-center justify-center"
                        aria-expanded={open}
                        aria-label={`${open ? "Collapse" : "Expand"} ${group.name}`}
                        onClick={() => setExpanded((prev) => ({ ...prev, [group.key]: !open }))}
                    >
                        <IconChevronDown size={16} open={open} />
                    </button>
                </Box>
                {open && (
                    <Box className="flex flex-col gap-2 pb-3" sx={{ pl: `${ROW_INDENT}px` }}>
                        {group.rows.map((row, i) =>
                            renderRow(row, { first: i === 0, last: i === group.rows.length - 1 })
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    const renderBody = () => {
        if (loadState === "loading") {
            return (
                <Box className="flex items-center justify-center py-6">
                    <DotLottleLoader sx={{ width: 80, height: 80 }} fill={false} />
                </Box>
            );
        }
        if (loadState === "error") {
            return (
                <Box className="flex flex-col items-center gap-2 py-6 text-center">
                    <Typography sx={{ fontSize: 13, color: "var(--text-2)" }}>
                        Couldn&apos;t load this project&apos;s videos.
                    </Typography>
                    <Button
                        onClick={() => loadProject()}
                        sx={{ textTransform: "none", fontFamily: "CircularStd", color: "var(--accent-text)" }}
                    >
                        Try again
                    </Button>
                </Box>
            );
        }
        if (!otherRows) {
            return (
                <Typography className="text-center py-6" sx={{ fontSize: 13, color: "var(--text-2)" }}>
                    There are no other delivered videos on this project yet.
                </Typography>
            );
        }
        return (
            <>
                <label
                    className={classNames(
                        "flex items-center gap-3 pt-4 pb-3.5 border-b border-border-token",
                        totalEnabled > 0 ? "cursor-pointer" : "cursor-default"
                    )}
                >
                    <Checkbox
                        size="small"
                        disableRipple
                        checked={allSelected}
                        indeterminate={n > 0 && !allSelected}
                        disabled={totalEnabled === 0}
                        onChange={toggleAll}
                        sx={CHECK_SX}
                    />
                    <span className="flex-1 text-[12.5px] font-bold text-text-1">Select all videos</span>
                    <span className="text-[12px] text-text-2 tabular-nums">
                        {n} of {totalEnabled} selected
                    </span>
                </label>
                {/* Legacy projects have no channels: one flat list under Select all. */}
                {hasChannels ? (
                    <Box className="flex flex-col pt-2">{groups.map(renderGroup)}</Box>
                ) : (
                    <Box className="flex flex-col gap-2 pt-3">
                        {groups.flatMap((g) => g.rows).map((row) => renderRow(row))}
                    </Box>
                )}
            </>
        );
    };

    return (
        <Dialog
            open
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            fullScreen={isMobile}
            scroll="paper"
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : "16px",
                    // Full screen below 768px must not keep the desktop cap.
                    maxWidth: isMobile ? "100%" : 640,
                    background: "var(--surface)",
                    backgroundImage: "none"
                }
            }}
        >
            <DialogTitle sx={{ p: 0 }}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    px={{ xs: 2, md: 3 }}
                    py={2}
                    borderBottom="1px solid var(--border)"
                >
                    <Typography
                        sx={{ fontSize: 18, fontWeight: 700, letterSpacing: -0.3, fontFamily: "CircularStd", color: "var(--text)" }}
                    >
                        Apply comment to other videos
                    </Typography>
                    <IconButton size="small" aria-label="Close" onClick={handleClose} disabled={submitting}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent
                sx={{
                    p: { xs: "16px", sm: "20px 24px" },
                    // MUI zeroes the top padding of content that follows a title.
                    ".MuiDialogTitle-root + &": { pt: { xs: "16px", sm: "20px" } }
                }}
            >
                <Box className="rounded-xl bg-surface-2" sx={{ borderLeft: "3px solid var(--accent)", p: "12px 14px" }}>
                    <Box className="flex items-center gap-2 min-w-0">
                        <span className="font-bold text-text-1 truncate min-w-0" style={{ fontSize: 13 }}>
                            {author}
                        </span>
                        {typeof comment.timestamp_end === "number" && (
                            <span className="text-accent-text font-medium shrink-0 tabular-nums" style={{ fontSize: 12 }}>
                                {`${toMmSs(comment.timestamp || 0)}–${toMmSs(comment.timestamp_end)}`}
                            </span>
                        )}
                    </Box>
                    <Typography
                        sx={{
                            mt: 0.5,
                            fontSize: 13,
                            color: "var(--text-2)",
                            whiteSpace: "pre-line",
                            overflowWrap: "anywhere",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden"
                        }}
                    >
                        <MentionText text={comment.message} />
                    </Typography>
                </Box>
                {renderBody()}
            </DialogContent>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    p: { xs: "14px 16px", sm: "14px 28px" },
                    borderTop: "1px solid var(--border)",
                    background: "var(--surface)",
                    flexShrink: 0
                }}
            >
                <Button
                    onClick={handleClose}
                    disabled={submitting}
                    sx={{ textTransform: "none", fontFamily: "CircularStd", color: "var(--text)" }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    disableElevation
                    disabled={!n || submitting}
                    onClick={handleApply}
                    startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : undefined}
                    sx={{ borderRadius: "24px", px: 3, textTransform: "none", fontFamily: "CircularStd" }}
                >
                    {n ? `Apply to ${pluralVideos(n)}` : "Apply"}
                </Button>
            </Box>
        </Dialog>
    );
};
